import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  InputAdornment,
  useTheme,
  Breadcrumbs,
  CircularProgress,
  Backdrop,
  Fade,
  tooltipClasses,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from "@mui/material";
import Poetlogopen from "../static/POET_Logo_Pen.png";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ContentCopy,
  Download,
  Save,
  AutoAwesome,
  Visibility,
  Refresh,
  Close,
  Delete,
  Search,
  Star,
  StarBorder,
  ChatBubbleOutline,
  RocketLaunch,
  Psychology,
  Palette,
  Insights,
  LocalOffer,
  Fullscreen,
  FullscreenExit,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  AutoFixHigh,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import newBlogService from "../services/new_blogsServices";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import jwtDecode from "jwt-decode";
import { 
  saveBusinessToolContent,
  getBusinessToolsHistory,
  deleteBusinessToolContent,
  toggleStarBusinessTool,
} from "../services/businessToolsHistoryService";

const ModernTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }) => {
            state.styles.popper.zIndex = 99999;
          },
        },
      ],
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
    color:
      theme.palette.mode === "light"
        ? theme.palette.primary.main
        : theme.palette.common.white,
    boxShadow: theme.shadows[6],
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 10,
    padding: "8px 14px",
    backdropFilter: "blur(6px)",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
  },
}));

const tools = [
  {
    id: "slogan",
    name: "Slogan Generator",
    icon: <LocalOffer />,
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    questions: [
      "Tell us about your brand or business",
      "What are your business's unique selling products/features?",
      "Who is your target audience or user base?",
    ],
    placeholder: "Describe your business details...",
    description: "Create catchy and memorable slogans for your brand",
  },
  {
    id: "strategy",
    name: "Brand Strategy",
    icon: <Insights />,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    questions: [
      "Tell us about your brand or business",
      "What is your brand's or company's mission?",
    ],
    placeholder: "Describe your brand vision...",
    description: "Develop comprehensive brand strategies and positioning",
  },
  {
    id: "idea",
    name: "Business Idea Generator",
    icon: <RocketLaunch />,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    questions: [
      "What industries or areas are you interested in?",
      "What is your available budget range?",
    ],
    placeholder: "Share your interests and budget...",
    description: "Generate innovative business ideas based on your interests",
  },
  {
    id: "persona",
    name: "Persona Generator",
    icon: <Psychology />,
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
    questions: ["What is your company name?", "Tell us about your company"],
    placeholder: "Describe your company...",
    description: "Create detailed customer personas for your business",
  },
];

// Function to format generated content with titles and emojis
const formatGeneratedContent = (content, toolId) => {
  if (!content) return "";

  const emojiMap = {
    slogan: ["🚀", "💡", "✨", "🌟", "🔥", "🎯", "💎", "⚡"],
    strategy: ["📊", "🎯", "🚀", "💼", "🏆", "⭐", "📈", "🎨"],
    idea: ["💡", "🚀", "🌟", "💰", "🏢", "🛠️", "🎯", "✨"],
    persona: ["👤", "🎯", "💼", "👥", "📊", "🤝", "💡", "🌟"],
  };

  const toolEmojis = emojiMap[toolId] || ["✨", "💡", "🌟"];

  const getRandomEmoji = () => {
    return toolEmojis[Math.floor(Math.random() * toolEmojis.length)];
  };

  switch (toolId) {
    case "slogan":
      return content
        .split("\n")
        .map((line) => {
          if (line.trim() && !line.includes("**") && line.length > 10) {
            return `${getRandomEmoji()} **${line.trim()}**`;
          }
          return line;
        })
        .join("\n");

    case "strategy":
      const sections = content.split("\n\n");
      return sections
        .map((section) => {
          const lines = section.split("\n");
          if (lines[0] && lines[0].includes(":")) {
            const [title, ...contentLines] = lines[0].split(":");
            return `${getRandomEmoji()} **${title.trim()}:**${contentLines.join(":")}\n${lines.slice(1).join("\n")}`;
          }
          return section;
        })
        .join("\n\n");

    case "idea":
      return content
        .split("\n")
        .map((line) => {
          if (line.trim() && line.includes(":")) {
            const [title, ...content] = line.split(":");
            return `${getRandomEmoji()} **${title.trim()}:** ${content.join(":").trim()}`;
          } else if (line.trim() && line.length > 20) {
            return `${getRandomEmoji()} **${line.trim()}**`;
          }
          return line;
        })
        .join("\n");

    case "persona":
      return content
        .split("\n")
        .map((line) => {
          if (line.trim() && line.includes(":")) {
            const [title, ...content] = line.split(":");
            const emoji = title.toLowerCase().includes("name")
              ? "👤"
              : title.toLowerCase().includes("age")
                ? "🎂"
                : title.toLowerCase().includes("occupation")
                  ? "💼"
                  : title.toLowerCase().includes("income")
                    ? "💰"
                    : title.toLowerCase().includes("goals")
                      ? "🎯"
                      : title.toLowerCase().includes("pain")
                        ? "😟"
                        : title.toLowerCase().includes("channels")
                          ? "📱"
                          : title.toLowerCase().includes("motivation")
                            ? "💪"
                            : getRandomEmoji();
            return `${emoji} **${title.trim()}:** ${content.join(":").trim()}`;
          }
          return line;
        })
        .join("\n");

    default:
      return content;
  }
};

// Function to render formatted content with styles
const renderFormattedContent = (content) => {
  if (!content) return null;

  const lines = content.split("\n");
  return lines.map((line, index) => {
    if (line.includes("**")) {
      const parts = line.split("**");
      return (
        <Box key={index} sx={{ mb: 1.5 }}>
          {parts.map((part, partIndex) => {
            if (partIndex % 2 === 1) {
              return (
                <Typography
                  key={partIndex}
                  component="span"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    fontSize: "1.1rem",
                    mr: 0.5,
                  }}
                >
                  {part}
                </Typography>
              );
            } else {
              return (
                <Typography
                  key={partIndex}
                  component="span"
                  sx={{
                    color: "text.primary",
                    fontSize: "1rem",
                  }}
                >
                  {part}
                </Typography>
              );
            }
          })}
        </Box>
      );
    }

    return (
      <Typography
        key={index}
        sx={{
          mb: 1,
          color: "text.primary",
          fontSize: "1rem",
          lineHeight: 1.6,
        }}
      >
        {line || <br />}
      </Typography>
    );
  });
};

const toolIcons = {
  slogan: <LocalOffer />,
  strategy: <Insights />,
  idea: <RocketLaunch />,
  persona: <Psychology />,
};

const toolColors = {
  slogan: "#6366f1",
  strategy: "#10b981",
  idea: "#f59e0b",
  persona: "#8b5cf6",
};

const AIBusinessTools = () => {
  const theme = useTheme();
  const [activeTool, setActiveTool] = useState(tools[0]);
  const [answers, setAnswers] = useState(
    Array(tools[0].questions.length).fill(""),
  );
  const [generatedContent, setGeneratedContent] = useState("");
  const [formattedContent, setFormattedContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTool, setLoadingTool] = useState(null);

  // User identity
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentMachineId, setCurrentMachineId] = useState(null);
  
  // Saved content state
  const [savedContents, setSavedContents] = useState([]);
  const [filteredSavedContents, setFilteredSavedContents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [starredItems, setStarredItems] = useState(new Set());

  // Restore and tool switch state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [pendingRestoreItem, setPendingRestoreItem] = useState(null);
  const [pendingToolSwitch, setPendingToolSwitch] = useState(null);
  
  // Store pending restore data to apply after tool change
  const [pendingAnswers, setPendingAnswers] = useState(null);
  const [pendingGeneratedContent, setPendingGeneratedContent] = useState(null);
  const [pendingFormattedContent, setPendingFormattedContent] = useState(null);

  // Track if current data is from a saved/restored item
  const [isDataFromSaved, setIsDataFromSaved] = useState(false);
  const [originalAnswers, setOriginalAnswers] = useState([]);
  const [originalGeneratedContent, setOriginalGeneratedContent] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);

  // Get user identity from token
  const getIdentityFromToken = () => {
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      if (decoded.role === "guest") {
        return { machineId: decoded.machineId };
      } else {
        return { userId: decoded._id };
      }
    } catch (err) {
      console.error("Invalid token");
      return null;
    }
  };

  useEffect(() => {
    const identity = getIdentityFromToken();
    if (identity) {
      setCurrentUserId(identity.userId);
      setCurrentMachineId(identity.machineId);
    }
  }, []);

  // Check if current data has been modified from the saved/original state
  const hasUnsavedChanges = () => {
    // If data is from saved/restored source, check if modified
    if (isDataFromSaved) {
      const answersChanged = JSON.stringify(answers) !== JSON.stringify(originalAnswers);
      const contentChanged = generatedContent !== originalGeneratedContent;
      return answersChanged || contentChanged;
    }
    // If data is new (not from saved), check if any data exists
    const hasAnswers = answers.some(a => a.trim() !== "");
    const hasContent = generatedContent.trim() !== "";
    return hasAnswers || hasContent;
  };

  // Save current state as "original" after restore
  const markAsSavedState = (answersToSave, contentToSave) => {
    setOriginalAnswers([...answersToSave]);
    setOriginalGeneratedContent(contentToSave);
    setIsDataFromSaved(true);
  };

  // Reset "original" tracking for new data
  const resetSavedState = () => {
    setIsDataFromSaved(false);
    setOriginalAnswers([]);
    setOriginalGeneratedContent("");
  };

  // Handle restore with confirmation
  const handleRestoreClick = (item) => {
    if (hasUnsavedChanges()) {
      setPendingRestoreItem(item);
      setRestoreDialogOpen(true);
    } else {
      performRestore(item);
    }
  };

  // Handle tool switch
  const handleToolClick = (tool) => {
    if (hasUnsavedChanges()) {
      setPendingToolSwitch(tool);
      setRestoreDialogOpen(true);
    } else {
      switchToTool(tool);
    }
  };

  // Switch to a different tool without any data
  const switchToTool = (tool) => {
    setActiveTool(tool);
    setGeneratedContent("");
    setFormattedContent("");
    setShowEditorToolbar(false);
    resetSavedState();
  };

  // Perform actual restore - sets pending data and triggers tool change
  // const performRestore = (item) => {
  //   // Find the target tool
  //   const targetTool = tools.find(t => t.id === item.toolId);
    
  //   if (targetTool) {
  //     // Prepare answers based on tool type
  //     const inputData = item.inputData;
  //     let newAnswers = [];
      
  //     if (item.toolId === "slogan") {
  //       newAnswers = [
  //         inputData.brand_business || "",
  //         inputData.usp_features || "",
  //         inputData.target_audience || "",
  //       ];
  //     } else if (item.toolId === "strategy") {
  //       newAnswers = [
  //         inputData.brand_business || "",
  //         inputData.mission || "",
  //       ];
  //     } else if (item.toolId === "idea") {
  //       newAnswers = [
  //         inputData.industry_interest || "",
  //         inputData.budget_range || "",
  //       ];
  //     } else if (item.toolId === "persona") {
  //       newAnswers = [
  //         inputData.company_name || "",
  //         inputData.company_details || "",
  //       ];
  //     }
      
  //     // Store pending data to apply after tool change
  //     setPendingAnswers(newAnswers);
  //     setPendingGeneratedContent(item.generatedContent);
  //     setPendingFormattedContent(item.formattedContent || item.generatedContent);
      
  //     // If same tool, apply immediately
  //     if (activeTool.id === item.toolId) {
  //       applyPendingRestore();
  //     } else {
  //       // Change the active tool
  //       setActiveTool(targetTool);
  //     }
      
  //     setSnackbar({
  //       open: true,
  //       message: `📋 Restoring ${targetTool.name} content...`,
  //       severity: "info",
  //     });
  //   } else {
  //     setSnackbar({
  //       open: true,
  //       message: "Error: Tool not found",
  //       severity: "error",
  //     });
  //   }
  // };

  // Perform actual restore - sets pending data and triggers tool change
const performRestore = (item) => {
  // Find the target tool
  const targetTool = tools.find(t => t.id === item.toolId);
  
  if (targetTool) {
    // Prepare answers based on tool type
    const inputData = item.inputData;
    let newAnswers = [];
    
    if (item.toolId === "slogan") {
      newAnswers = [
        inputData.brand_business || "",
        inputData.usp_features || "",
        inputData.target_audience || "",
      ];
    } else if (item.toolId === "strategy") {
      newAnswers = [
        inputData.brand_business || "",
        inputData.mission || "",
      ];
    } else if (item.toolId === "idea") {
      newAnswers = [
        inputData.industry_interest || "",
        inputData.budget_range || "",
      ];
    } else if (item.toolId === "persona") {
      newAnswers = [
        inputData.company_name || "",
        inputData.company_details || "",
      ];
    }
    
    // If same tool, apply directly without pending state
    if (activeTool.id === item.toolId) {
      // Apply restore directly
      setAnswers(newAnswers);
      setGeneratedContent(item.generatedContent);
      setFormattedContent(item.formattedContent || item.generatedContent);
      setShowEditorToolbar(true);
      markAsSavedState(newAnswers, item.generatedContent || "");
      
      setSnackbar({
        open: true,
        message: `✅ ${targetTool.name} content restored successfully!`,
        severity: "success",
      });
    } else {
      // Different tool - store pending data and change tool
      setPendingAnswers(newAnswers);
      setPendingGeneratedContent(item.generatedContent);
      setPendingFormattedContent(item.formattedContent || item.generatedContent);
      setActiveTool(targetTool);
      
      setSnackbar({
        open: true,
        message: `📋 Switching to ${targetTool.name} and restoring content...`,
        severity: "info",
      });
    }
  } else {
    setSnackbar({
      open: true,
      message: "Error: Tool not found",
      severity: "error",
    });
  }
};

  // Apply pending restore data
  // const applyPendingRestore = () => {
  //   if (pendingAnswers !== null) {
  //     // Set the answers
  //     setAnswers(pendingAnswers);
  //     // Set the generated content
  //     setGeneratedContent(pendingGeneratedContent || "");
  //     setFormattedContent(pendingFormattedContent || pendingGeneratedContent || "");
  //     setShowEditorToolbar(true);
      
  //     // Mark this as saved/restored data
  //     markAsSavedState(pendingAnswers, pendingGeneratedContent || "");
      
  //     // Clear pending data
  //     setPendingAnswers(null);
  //     setPendingGeneratedContent(null);
  //     setPendingFormattedContent(null);
      
  //     setSnackbar({
  //       open: true,
  //       message: `✅ ${activeTool.name} content restored successfully!`,
  //       severity: "success",
  //     });
  //   }
  // };
const applyPendingRestore = () => {
  if (pendingAnswers !== null) {
    // Set the answers
    setAnswers(pendingAnswers);
    // Set the generated content
    setGeneratedContent(pendingGeneratedContent || "");
    setFormattedContent(pendingFormattedContent || pendingGeneratedContent || "");
    setShowEditorToolbar(true);
    
    // Mark this as saved/restored data
    markAsSavedState(pendingAnswers, pendingGeneratedContent || "");
    
    // Get the tool name for the snackbar
    const targetTool = tools.find(t => t.id === activeTool.id);
    
    // Clear pending data
    setPendingAnswers(null);
    setPendingGeneratedContent(null);
    setPendingFormattedContent(null);
    
    setSnackbar({
      open: true,
      message: `✅ ${targetTool?.name || activeTool.name} content restored successfully!`,
      severity: "success",
    });
  }
};

  // Effect to apply pending restore data when tool changes
  useEffect(() => {
    if (pendingAnswers !== null) {
      applyPendingRestore();
    }
  }, [activeTool]);

  
  // Fetch saved content from database
  const fetchSavedContent = async () => {
    try {
      const response = await getBusinessToolsHistory(currentUserId, currentMachineId);
      if (response.success) {
        setSavedContents(response.data);
        // Update starred items set
        const starred = new Set();
        response.data.forEach(item => {
          if (item.isStarred) starred.add(item._id);
        });
        setStarredItems(starred);
        filterContentByTab(response.data, selectedTab, searchTerm);
      }
    } catch (error) {
      console.error("Error fetching saved content:", error);
    }
  };

  const filterContentByTab = (data, tab, search) => {
    let filtered = [...data];
    
    // Filter by tool if not "all" tab
    if (tab !== 0) {
      const toolMap = { 1: "slogan", 2: "strategy", 3: "idea", 4: "persona", 5: "starred" };
      if (tab === 5) {
        // Starred tab - show only starred items
        filtered = filtered.filter(item => item.isStarred === true);
      } else {
        filtered = filtered.filter(item => item.toolId === toolMap[tab]);
      }
    }
    
    // Filter by search term
    if (search) {
      filtered = filtered.filter(
        (item) =>
          item.toolName?.toLowerCase().includes(search.toLowerCase()) ||
          item.generatedContent?.toLowerCase().includes(search.toLowerCase()) ||
          item.title?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    setFilteredSavedContents(filtered);
  };

  useEffect(() => {
    if (currentUserId || currentMachineId) {
      fetchSavedContent();
    }
  }, [currentUserId, currentMachineId]);

  // Filter saved content when tab or search changes
  useEffect(() => {
    filterContentByTab(savedContents, selectedTab, searchTerm);
  }, [searchTerm, savedContents, selectedTab]);

  const extractContent = (res) => {
    return res?.data?.content || "";
  };

  const handleBreadcrumbClick = (event, path) => {
    event.preventDefault();
    window.location.href = "/docTags";
  };

  // Reset answers when tool changes (but not during restore)
  useEffect(() => {
    // Only reset if there's no pending restore and not from saved state
    if (pendingAnswers === null && !isDataFromSaved) {
      setAnswers(Array(activeTool.questions.length).fill(""));
      setGeneratedContent("");
      setFormattedContent("");
      setShowEditorToolbar(false);
    }
  }, [activeTool]);

  useEffect(() => {
    if (generatedContent && !pendingAnswers) {
      const formatted = formatGeneratedContent(generatedContent, activeTool.id);
      setFormattedContent(formatted);
    }
  }, [generatedContent, activeTool.id]);

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const startLoading = (toolId) => {
    setLoading(true);
    setLoadingTool(toolId);
  };

  const stopLoading = () => {
    setLoading(false);
    setLoadingTool(null);
  };

  // Save current content to history
  const handleSaveToHistory = async () => {
    if (!generatedContent) {
      setSnackbar({
        open: true,
        message: "Generate content first!",
        severity: "warning",
      });
      return;
    }

    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before saving!",
        severity: "warning",
      });
      return;
    }

    const inputData = {};
    if (activeTool.id === "slogan") {
      inputData.brand_business = answers[0];
      inputData.usp_features = answers[1];
      inputData.target_audience = answers[2];
    } else if (activeTool.id === "strategy") {
      inputData.brand_business = answers[0];
      inputData.mission = answers[1];
    } else if (activeTool.id === "idea") {
      inputData.industry_interest = answers[0];
      inputData.budget_range = answers[1];
    } else if (activeTool.id === "persona") {
      inputData.company_name = answers[0];
      inputData.company_details = answers[1];
    }

    const identity = getIdentityFromToken();

    try {
      const result = await saveBusinessToolContent({
        userId: identity?.userId || null,
        machineId: identity?.machineId || null,
        toolId: activeTool.id,
        toolName: activeTool.name,
        inputData: inputData,
        generatedContent: generatedContent,
        formattedContent: formattedContent,
        title: generatedContent.substring(0, 50),
      });

      if (result.success) {
        // After saving, mark current state as saved
        markAsSavedState(answers, generatedContent);
        
        setSnackbar({
          open: true,
          message: "💾 Content saved to history successfully!",
          severity: "success",
        });
        setSaveDialogOpen(true);
        fetchSavedContent();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to save content",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving content:", error);
      setSnackbar({
        open: true,
        message: "Failed to save content",
        severity: "error",
      });
    }
  };

  // Delete saved content
  const handleDeleteSaved = async (id) => {
    try {
      await deleteBusinessToolContent(id);
      setSnackbar({
        open: true,
        message: "🗑️ Content deleted successfully!",
        severity: "success",
      });
      fetchSavedContent();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete content",
        severity: "error",
      });
    }
  };

  // Toggle star status
  const handleToggleStar = async (id, currentStarred) => {
    try {
      await toggleStarBusinessTool(id, !currentStarred);
      setSnackbar({
        open: true,
        message: currentStarred ? "Removed from favorites" : "Added to favorites",
        severity: "success",
      });
      fetchSavedContent();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update favorite status",
        severity: "error",
      });
    }
  };

  const generateSloganFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("slogan");

      const payload = {
        brand_business: answers[0],
        usp_features: answers[1],
        target_audience: answers[2],
        tone: "friendly",
        language: "English",
      };

      const res = await newBlogService.generateSlogan(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);
        // After generating new content, it's not from saved state
        resetSavedState();

        setSnackbar({
          open: true,
          message: "✨ Slogans generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate slogans",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generateBrandStrategyFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("strategy");

      const payload = {
        brand_business: answers[0],
        mission: answers[1],
        tone: "Professional",
        language: "English",
      };

      const res = await newBlogService.generateBrandStrategy(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);
        resetSavedState();

        setSnackbar({
          open: true,
          message: "✨ Brand strategy generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate brand strategy",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generateBusinessIdeaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("idea");

      const payload = {
        industry_interest: answers[0],
        budget_range: answers[1],
        tone: "friendly",
        language: "English",
      };

      const res = await newBlogService.generateBusinessIdea(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);
        resetSavedState();

        setSnackbar({
          open: true,
          message: "✨ Business ideas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate business ideas",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generatePersonaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("persona");

      const payload = {
        company_name: answers[0],
        company_details: answers[1],
        tone: "professional",
        language: "English",
      };

      const res = await newBlogService.generatePersona(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);
        resetSavedState();

        setSnackbar({
          open: true,
          message: "✨ Personas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate personas",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setSnackbar({
      open: true,
      message: "📋 Copied to clipboard!",
      severity: "success",
    });
  };

  const handleCopyFormatted = () => {
    navigator.clipboard.writeText(formattedContent);
    setSnackbar({
      open: true,
      message: "📋 Formatted content copied!",
      severity: "success",
    });
  };

  const handleDownload = () => {
    if (!generatedContent) return;

    const element = document.createElement("a");
    const file = new Blob([generatedContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${activeTool.name.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    setSnackbar({
      open: true,
      message: "📥 Download started!",
      severity: "success",
    });
  };

  const handleClear = () => {
    setAnswers(Array(activeTool.questions.length).fill(""));
    setGeneratedContent("");
    setFormattedContent("");
    setShowEditorToolbar(false);
    resetSavedState();
  };

  const formatText = (format) => {
    const textarea = document.querySelector("textarea");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = generatedContent.substring(start, end);
    let formattedText = "";

    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "bullet":
        formattedText = `\n• ${selectedText}`;
        break;
      case "number":
        formattedText = `\n1. ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent =
      generatedContent.substring(0, start) +
      formattedText +
      generatedContent.substring(end);
    setGeneratedContent(newContent);
  };

  const handleFullscreenFormatText = (format) => {
    const editor = document.querySelector("#fullscreen-editor");
    if (!editor) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (!selectedText) return;

    let formattedText = "";
    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      default:
        formattedText = selectedText;
    }

    const newNode = document.createTextNode(formattedText);
    range.deleteContents();
    range.insertNode(newNode);

    const newFormattedContent = editor.textContent || "";
    setFormattedContent(newFormattedContent);

    const rawContent = newFormattedContent
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .replace(
        /✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g,
        "",
      )
      .trim();
    setGeneratedContent(rawContent);
  };

  const handleAddEmoji = () => {
    const emojiMap = {
      slogan: ["🚀", "💡", "✨", "🌟", "🔥", "🎯", "💎", "⚡"],
      strategy: ["📊", "🎯", "🚀", "💼", "🏆", "⭐", "📈", "🎨"],
      idea: ["💡", "🚀", "🌟", "💰", "🏢", "🛠️", "🎯", "✨"],
      persona: ["👤", "🎯", "💼", "👥", "📊", "🤝", "💡", "🌟"],
    };
    const toolEmojis = emojiMap[activeTool.id] || ["✨", "💡", "🌟"];
    const randomEmoji =
      toolEmojis[Math.floor(Math.random() * toolEmojis.length)];

    const editor = document.querySelector("#fullscreen-editor");
    if (!editor) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const newNode = document.createTextNode(`${randomEmoji} `);
    range.insertNode(newNode);

    const newFormattedContent = editor.textContent || "";
    setFormattedContent(newFormattedContent);

    const rawContent = newFormattedContent
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .replace(
        /✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g,
        "",
      )
      .trim();
    setGeneratedContent(rawContent);
  };

  // Fullscreen Loader Component
  const FullscreenLoader = ({ tool }) => (
    <Fade in={loading && loadingTool === tool.id} timeout={300}>
      <Backdrop
        open={loading && loadingTool === tool.id}
        sx={{
          zIndex: 9999,
          color: "#fff",
          background: tool.gradient,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          backdropFilter: "blur(10px)",
          backgroundColor: alpha(tool.color, 0.95),
        }}
      >
        <Box sx={{ textAlign: "center", zIndex: 1, maxWidth: 500, mx: "auto", px: 2 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${alpha("#fff", 0.2)}, transparent)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 32px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                animation: "rotate 3s linear infinite",
                "@keyframes rotate": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            >
              <Box
                sx={{
                  animation: "counterRotate 3s linear infinite",
                  "@keyframes counterRotate": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(-360deg)" },
                  },
                }}
              >
                {React.cloneElement(tool.icon, {
                  sx: { fontSize: 48, color: "#fff" },
                })}
              </Box>
            </Box>
          </Box>

          <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
            Crafting Your {tool.name}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, mb: 3 }}>
            Our AI is generating unique content tailored for your needs
          </Typography>

          <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", mb: 4 }}>
            <Box sx={{ width: "100%", height: 4, backgroundColor: alpha("#fff", 0.2), borderRadius: 2, overflow: "hidden" }}>
              <Box
                sx={{
                  width: "75%",
                  height: "100%",
                  background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.5))",
                  borderRadius: 2,
                  animation: "progressShimmer 2s ease-in-out infinite",
                  "@keyframes progressShimmer": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(400%)" },
                  },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1, opacity: 0.7 }}>
              This usually takes 10-30 seconds
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
            {[...Array(3)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  animation: `bounce 1.4s infinite ${i * 0.2}s`,
                  "@keyframes bounce": {
                    "0%, 80%, 100%": { transform: "scale(0)", opacity: 0.5 },
                    "40%": { transform: "scale(1)", opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Backdrop>
    </Fade>
  );

  const CardLoader = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: alpha("#fff", 0.92),
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        borderRadius: 3,
      }}
    >
      <Box sx={{ textAlign: "center", px: 3 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            margin: "0 auto 16px",
            position: "relative",
            background: `conic-gradient(${activeTool.color} 0deg, ${alpha(activeTool.color, 0.3)} 360deg)`,
            animation: "spin 1s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 4,
              background: "#fff",
              borderRadius: "50%",
            },
          }}
        >
          <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}>
            {React.cloneElement(activeTool.icon, { sx: { fontSize: 24, color: activeTool.color } })}
          </Box>
        </Box>
        <Typography variant="h6" color="text.primary" fontWeight="bold" sx={{ mb: 1 }}>
          Generating {activeTool.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mx: "auto" }}>
          Please wait while our AI creates amazing content for you...
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        minHeight: "100vh",
        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#0f172a",
        position: "relative",
      }}
    >
      {tools.map((tool) => (
        <FullscreenLoader key={tool.id} tool={tool} />
      ))}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              mr: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <img
              alt="logo"
              src={Poetlogopen}
              style={{
                height: "28px",
                width: "28px",
                objectFit: "contain",
                bgcolor: 'white',
                filter: "drop-shadow(0 0 7px rgba(163, 163, 164, 0.65))",
              }}
            />
          </Box>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "#667eea" }} />}>
            <Box
              component={RouterLink}
              to="/docTags"
              onClick={(e) => handleBreadcrumbClick(e, "/docTags")}
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                color: "#667eea",
                fontWeight: 500,
                fontSize: "0.95rem",
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.12)",
                  color: "#764ba2",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <AutoFixHigh sx={{ fontSize: 20, mr: 1 }} />
              MultiTool AI
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", padding: "6px 12px", borderRadius: "6px", backgroundColor: "rgba(102, 126, 234, 0.08)" }}>
              <AutoAwesome sx={{ fontSize: 20, color: "#667eea", mr: 1 }} />
              <Typography sx={{ color: "#1a1a1a", fontWeight: 600, fontSize: "0.95rem" }}>
                AI Business Tools
              </Typography>
            </Box>
          </Breadcrumbs>
        </Box>
      </Box>

      <Box sx={{ textAlign: "center", mb: 2, mt: -6, position: "relative", maxWidth: 800, mx: "auto" }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f59e0b 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 2,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          AI Business Tools
        </Typography>
        <Typography variant="h6" sx={{ color: "text.secondary", maxWidth: 800, mx: "auto", lineHeight: 1.5, fontWeight: 80 }}>
          Generate Professional-grade business insights, marketing strategies, and growth ideas tailored for entrepreneurs and business leaders
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Panel - Tools & Questions */}
        <Grid xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
              position: "relative",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)}, 0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}`,
              overflow: "hidden",
            }}
          >
            {loading && loadingTool === activeTool.id && <CardLoader />}

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Palette sx={{ color: activeTool.color }} />
                Select AI Tool
              </Typography>
              <Grid container spacing={2}>
                {tools.map((tool) => (
                  <Grid xs={6} sm={3} key={tool.id}>
                    <Card
                      onClick={() => handleToolClick(tool)}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border: activeTool.id === tool.id ? `2px solid ${tool.color}` : "1px solid",
                        borderColor: activeTool.id === tool.id ? tool.color : "divider",
                        backgroundColor: activeTool.id === tool.id ? alpha(tool.color, 0.05) : "background.paper",
                        borderRadius: 2,
                        height: "100%",
                        minHeight: 120,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "4px",
                          background: tool.gradient,
                          opacity: activeTool.id === tool.id ? 1 : 0,
                          transition: "opacity 0.3s ease",
                        },
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: `0 12px 24px ${alpha(tool.color, 0.15)}`,
                          borderColor: tool.color,
                          "&::before": { opacity: 1 },
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: "center", p: 2, "&:last-child": { pb: 2 } }}>
                        <Box sx={{ display: "inline-flex", p: 1.5, borderRadius: "12px", background: tool.gradient, color: "white", mb: 1.5 }}>
                          {tool.icon}
                        </Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>{tool.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem" }}>
                          {tool.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(activeTool.color, 0.03),
                border: `1px solid ${alpha(activeTool.color, 0.1)}`,
                mb: 3,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: activeTool.gradient,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box sx={{ display: "inline-flex", p: 1.5, borderRadius: "12px", background: activeTool.gradient, color: "white", mr: 2 }}>
                  {activeTool.icon}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color={activeTool.color}>{activeTool.name}</Typography>
                  <Typography variant="body2" color="text.secondary">Answer these questions to generate amazing content</Typography>
                </Box>
              </Box>

              {activeTool.questions.map((question, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: activeTool.gradient, color: "white", display: "flex", alignItems: "center", justifyContent: "center", mr: 2, fontSize: "0.875rem", fontWeight: "bold" }}>
                      {index + 1}
                    </Box>
                    <Typography variant="body1" fontWeight="medium">{question}</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={activeTool.placeholder}
                    value={answers[index]}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: activeTool.color, borderWidth: 2 },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: activeTool.color, borderWidth: 2 },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ChatBubbleOutline sx={{ fontSize: 18, color: alpha(activeTool.color, 0.6) }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ))}

              <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={loading && loadingTool === activeTool.id ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                  onClick={() => {
                    if (activeTool.id === "slogan") generateSloganFromAPI();
                    else if (activeTool.id === "strategy") generateBrandStrategyFromAPI();
                    else if (activeTool.id === "idea") generateBusinessIdeaFromAPI();
                    else if (activeTool.id === "persona") generatePersonaFromAPI();
                  }}
                  size="large"
                  disabled={loading}
                  sx={{
                    flex: 1,
                    background: activeTool.gradient,
                    "&:hover": { background: activeTool.gradient, boxShadow: `0 8px 30px ${alpha(activeTool.color, 0.4)}`, transform: "translateY(-2px)" },
                    py: 1.5,
                    borderRadius: 1,
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  {loading && loadingTool === activeTool.id ? "Generating..." : "Generate Content"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleClear}
                  size="large"
                  disabled={loading}
                  sx={{ borderRadius: 1, borderColor: alpha(activeTool.color, 0.5), color: activeTool.color }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Generated Content & Actions */}
        <Grid xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Generated Content</Typography>
            </Box>

            {/* {showEditorToolbar && (
              <Paper elevation={0} sx={{ p: 1, mb: 2, borderRadius: 1, backgroundColor: alpha(theme.palette.primary.main, 0.05), display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <ModernTooltip title="Bold"><IconButton size="small" onClick={() => formatText("bold")}><FormatBold fontSize="small" /></IconButton></ModernTooltip>
                <ModernTooltip title="Italic"><IconButton size="small" onClick={() => formatText("italic")}><FormatItalic fontSize="small" /></IconButton></ModernTooltip>
                <Divider orientation="vertical" flexItem />
                <ModernTooltip title="Bullet List"><IconButton size="small" onClick={() => formatText("bullet")}><FormatListBulleted fontSize="small" /></IconButton></ModernTooltip>
                <ModernTooltip title="Numbered List"><IconButton size="small" onClick={() => formatText("number")}><FormatListNumbered fontSize="small" /></IconButton></ModernTooltip>
              </Paper>
            )} */}

            <Box sx={{ flex: 1, mb: 3, position: "relative", overflow: "auto" }}>
              {generatedContent ? (
                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: alpha(activeTool.color, 0.03), border: `1px solid ${alpha(activeTool.color, 0.1)}`, minHeight: 600, maxHeight: "calc(100vh - 400px)", overflow: "auto" }}>
                  <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${alpha(activeTool.color, 0.2)}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: activeTool.gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                        {activeTool.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color={activeTool.color}>{activeTool.name} Results</Typography>
                        <Typography variant="caption" color="text.secondary">Generated just now</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
                    {renderFormattedContent(formattedContent)}
                  </Box>
                  <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      {formattedContent.split(/\s+/).filter(Boolean).length} words • {formattedContent.length} characters
                    </Typography>
                    <Chip label="Formatted" size="small" icon={<AutoAwesome fontSize="small" />} sx={{ backgroundColor: alpha(activeTool.color, 0.1), color: activeTool.color }} />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, textAlign: "center", borderRadius: 2, backgroundColor: alpha(theme.palette.divider, 0.05), border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`, minHeight: 400 }}>
                  <AutoAwesome sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.3), mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>Generated Content</Typography>
                  <Typography variant="body2" color="text.secondary">Your AI-generated content will appear here. Fill in the questions on the left and click "Generate Content" to get started.</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
              <ModernTooltip title="Copy to clipboard">
                <Button variant="outlined" startIcon={<ContentCopy />} onClick={handleCopy} disabled={!generatedContent} fullWidth sx={{ borderRadius: 1, borderColor: alpha(activeTool.color, 0.3), color: activeTool.color }}>
                  Copy
                </Button>
              </ModernTooltip>
              <ModernTooltip title="Save for later">
                <Button variant="outlined" startIcon={<Save />} onClick={handleSaveToHistory} disabled={!generatedContent} fullWidth sx={{ borderRadius: 1, borderColor: alpha(activeTool.color, 0.3), color: activeTool.color }}>
                  Save
                </Button>
              </ModernTooltip>
              <ModernTooltip title="Download as text file">
                <Button variant="contained" startIcon={<Download />} onClick={handleDownload} disabled={!generatedContent} fullWidth sx={{ borderRadius: 1, background: activeTool.gradient }}>
                  Download
                </Button>
              </ModernTooltip>
            </Box>
          </Paper>
        </Grid>

        {/* Saved Content Section */}
        <Grid xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)}, 0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">Saved Content</Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search saved content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 250 }}
                />
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{ minHeight: 36 }}
                >
                  <Tab icon={<AutoAwesome />} label="All" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                  <Tab icon={<LocalOffer />} label="Slogans" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                  <Tab icon={<Insights />} label="Strategies" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                  <Tab icon={<RocketLaunch />} label="Ideas" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                  <Tab icon={<Psychology />} label="Personas" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                  <Tab icon={<Star />} label="Starred" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                </Tabs>
              </Box>
            </Box>

            {filteredSavedContents.length > 0 ? (
              <List>
                {filteredSavedContents.map((item, index) => (
                  <ListItem
                    key={item._id}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(toolColors[item.toolId], 0.1)}`,
                      backgroundColor: alpha(toolColors[item.toolId], 0.03),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: alpha(toolColors[item.toolId], 0.05),
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${alpha(toolColors[item.toolId], 0.1)}`,
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "10px",
                          background: `linear-gradient(135deg, ${toolColors[item.toolId]} 0%, ${alpha(toolColors[item.toolId], 0.7)} 100%)`,
                          color: "white",
                        }}
                      >
                        {toolIcons[item.toolId]}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.toolName}
                          </Typography>
                          <Chip
                            label={new Date(item.createdAt).toLocaleDateString()}
                            size="small"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                          {item.isStarred && (
                            <Star sx={{ color: "#fbbf24", fontSize: 16 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.generatedContent?.substring(0, 100)}...
                        </Typography>
                      }
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <ModernTooltip title="Restore">
                        <IconButton size="small" onClick={() => handleRestoreClick(item)}>
                          <Visibility sx={{ color: toolColors[item.toolId] }} />
                        </IconButton>
                      </ModernTooltip>
                      <ModernTooltip title={item.isStarred ? "Remove from favorites" : "Add to favorites"}>
                        <IconButton size="small" onClick={() => handleToggleStar(item._id, item.isStarred)}>
                          {item.isStarred ? (
                            <Star sx={{ color: "#fbbf24" }} />
                          ) : (
                            <StarBorder />
                          )}
                        </IconButton>
                      </ModernTooltip>
                      <ModernTooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteSaved(item._id)}>
                          <Delete fontSize="small" sx={{ color: "#f80000" }} />
                        </IconButton>
                      </ModernTooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.divider, 0.03),
                  border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`,
                }}
              >
                <Save
                  sx={{
                    fontSize: 48,
                    color: alpha(theme.palette.text.secondary, 0.3),
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No saved content yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate and save content to see it here
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Restore/Tool Switch Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon sx={{ color: "#f59e0b" }} />
          Warning: Unsaved Changes Will Be Lost
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes in the current form. If you proceed,
            your current input data and generated content will be replaced.
          </Typography>
          <Typography sx={{ mt: 2, color: "error.main", fontWeight: "bold" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRestoreDialogOpen(false);
            setPendingRestoreItem(null);
            setPendingToolSwitch(null);
          }} variant="outlined">
            Discard
          </Button>
          <Button 
            onClick={() => {
              if (pendingRestoreItem) {
                performRestore(pendingRestoreItem);
                setPendingRestoreItem(null);
              } else if (pendingToolSwitch) {
                switchToTool(pendingToolSwitch);
                setPendingToolSwitch(null);
              }
              setRestoreDialogOpen(false);
            }} 
            variant="contained" 
            color="primary"
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Dialog */}
      <Dialog fullScreen open={fullscreenDialogOpen} onClose={() => setFullscreenDialogOpen(false)} TransitionComponent={Fade}>
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Paper elevation={0} sx={{ p: 2.5, background: activeTool.gradient, color: "white", position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton onClick={() => setFullscreenDialogOpen(false)} sx={{ color: "white", backgroundColor: "rgba(255, 255, 255, 0.15)" }}>
                  <FullscreenExit />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "rgba(255, 255, 255, 0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {React.cloneElement(activeTool.icon, { sx: { fontSize: 24, color: "white" } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">{activeTool.name} - Fullscreen Editor</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Edit and refine your formatted content</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" startIcon={<ContentCopy />} onClick={handleCopyFormatted} size="small" sx={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "white" }}>
                  Copy
                </Button>
                <Button variant="contained" startIcon={<Download />} onClick={handleDownload} size="small" sx={{ backgroundColor: "white", color: activeTool.color }}>
                  Download
                </Button>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ flex: 1, display: "flex", overflow: "hidden", p: 3, gap: 3 }}>
            <Paper elevation={0} sx={{ flex: 3, display: "flex", flexDirection: "column", borderRadius: 3, overflow: "hidden", border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, backgroundColor: "background.paper" }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, backgroundColor: alpha(activeTool.color, 0.03), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color={activeTool.color}>Formatted Content Editor</Typography>
                  <Chip label="AI Generated" size="small" icon={<AutoAwesome fontSize="small" />} sx={{ backgroundColor: alpha(activeTool.color, 0.1), color: activeTool.color }} />
                </Box>
                <Typography variant="caption" color="text.secondary">{formattedContent.length} characters • {formattedContent.split(/\s+/).filter(Boolean).length} words</Typography>
              </Box>

              <Paper elevation={0} sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <ModernTooltip title="Bold"><IconButton size="small" onClick={() => handleFullscreenFormatText("bold")}><FormatBold fontSize="small" /></IconButton></ModernTooltip>
                <ModernTooltip title="Italic"><IconButton size="small" onClick={() => handleFullscreenFormatText("italic")}><FormatItalic fontSize="small" /></IconButton></ModernTooltip>
                <Divider orientation="vertical" flexItem />
                <Button size="small" startIcon={<AutoAwesome />} onClick={handleAddEmoji} sx={{ color: activeTool.color }}>Add Emoji</Button>
              </Paper>

              <Box sx={{ flex: 1, overflow: "auto", p: 3, backgroundColor: alpha(activeTool.color, 0.02) }}>
                <Box id="fullscreen-editor" contentEditable suppressContentEditableWarning onInput={(e) => {
                  const newContent = e.currentTarget.textContent || "";
                  setFormattedContent(newContent);
                  const rawContent = newContent.replace(/\*\*/g, "").replace(/\*/g, "").replace(/✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g, "").trim();
                  setGeneratedContent(rawContent);
                }} style={{ minHeight: "calc(100vh - 250px)", outline: "none", fontFamily: "'Inter', 'Roboto', sans-serif", fontSize: "1rem", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {renderFormattedContent(formattedContent)}
                </Box>
                {!formattedContent && (
                  <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: theme.palette.text.disabled }}>
                    <Typography variant="body1">Start editing your formatted content here...</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button variant="outlined" size="small" onClick={() => setFullscreenDialogOpen(false)} startIcon={<Close />}>Close</Button>
                <Button variant="contained" size="small" onClick={handleSaveToHistory} startIcon={<Save />} sx={{ background: activeTool.gradient }}>Save Changes</Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Generated Content</DialogTitle>
        <DialogContent>
          <Typography>Your content has been saved to your collection.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AIBusinessTools;